import { Color, ColorMatrixFilter } from 'pixi.js';
import { Config } from '../../../video-data/config';

import { BooleanAttribute, ColorAttribute, getAttributesToSerialize } from '../../attributes';
import { rgb2int } from '../../../libs/videoData';
import { comparer, observable, reaction } from '../../reactivity';
import { Filter } from '../Filter';
import type { BaseSettings } from '../../TimeAwareBase';

export class ColorChannelMapFilter extends Filter {
    override _classType = Config.COLOR_CHANNEL_MAP_FILTER;
    override pixiFilter: ColorMatrixFilter;

    @ColorAttribute()
    @observable
    accessor redChannelTargetColor = rgb2int([255, 0, 0]);
    @ColorAttribute()
    @observable
    accessor greenChannelTargetColor = rgb2int([0, 255, 0]);
    @ColorAttribute()
    @observable
    accessor blueChannelTargetColor = rgb2int([0, 0, 255]);
    @BooleanAttribute()
    @observable
    accessor isRedChannelMappedToAlpha = false;
    @BooleanAttribute()
    @observable
    accessor isGreenChannelMappedToAlpha = false;
    @BooleanAttribute()
    @observable
    accessor isBlueChannelMappedToAlpha = false;

    constructor(settings: BaseSettings) {
        super(settings);
        this.pixiFilter = new ColorMatrixFilter();

        this.addReaction(
            reaction(
                () => ({
                    red: this.redChannelTargetColor,
                    green: this.greenChannelTargetColor,
                    blue: this.blueChannelTargetColor,
                    redToAlpha: this.isRedChannelMappedToAlpha,
                    greenToAlpha: this.isGreenChannelMappedToAlpha,
                    blueToAlpha: this.isBlueChannelMappedToAlpha,
                }),
                (channels) => {
                    const [target1R, target1G, target1B] = new Color(channels.red).toRgbArray();
                    const [target2R, target2G, target2B] = new Color(channels.green).toRgbArray();
                    const [target3R, target3G, target3B] = new Color(channels.blue).toRgbArray();
                    const isAlphaApplied =
                        channels.redToAlpha || channels.greenToAlpha || channels.blueToAlpha;
                    this.pixiFilter.matrix = [
                        channels.redToAlpha ? 0 : target1R,
                        channels.greenToAlpha ? 0 : target2R,
                        channels.blueToAlpha ? 0 : target3R,
                        0,
                        0,
                        channels.redToAlpha ? 0 : target1G,
                        channels.greenToAlpha ? 0 : target2G,
                        channels.blueToAlpha ? 0 : target3G,
                        0,
                        0,
                        channels.redToAlpha ? 0 : target1B,
                        channels.greenToAlpha ? 0 : target2B,
                        channels.blueToAlpha ? 0 : target3B,
                        0,
                        0,
                        channels.redToAlpha ? 1 : 0,
                        channels.greenToAlpha ? 1 : 0,
                        channels.blueToAlpha ? 1 : 0,
                        isAlphaApplied ? 0 : 1,
                        0,
                    ];
                    this.pixiFilter.resources.colorMatrixUniforms.update();
                },
                { equals: comparer.shallow, name: 'ColorChannelMapFilter.matrix' },
            ),
        );
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(getAttributesToSerialize(this));
    }

    override isNeutral(): boolean {
        return (
            this.redChannelTargetColor === rgb2int([255, 0, 0]) &&
            this.greenChannelTargetColor === rgb2int([0, 255, 0]) &&
            this.blueChannelTargetColor === rgb2int([0, 0, 255]) &&
            this.isRedChannelMappedToAlpha === false &&
            this.isGreenChannelMappedToAlpha === false &&
            this.isBlueChannelMappedToAlpha === false
        );
    }
}
